import { SVG, Svg, G, Element as SvgElement, ClipPath
       } from '@svgdotjs/svg.js';
import { PlaceholderService
       } from 'app/collage-designer/stage/placeholder/placeholder.service';
import { CoordinateTranslation
       } from 'app/controls/image-cropper/coordinate-translation';
import { GalleryService } from 'app/gallery/gallery.service';
import { Point, Region } from 'core/geometry';
import { Icons } from 'core/icons';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { ImagePlaceholder } from './placeholder/image-placeholder';
import { TransformationManager } from './placeholder/transformation-manager';

interface Position
{
  clientX: number;
  clientY: number;
  pageX: number;
  pageY: number;
}

abstract class Source {
  public abstract getWrapper(): Svg;
  public abstract getElement(): HTMLElement;

  protected abstract dragStartAllowed(event: Event): boolean;
  protected abstract setDraggingImage(): void;
  protected abstract loadDraggingImage(targetPlaceholder: ImagePlaceholder):
      Promise<void>;
  protected placeholderService: PlaceholderService;
  protected galleryService: GalleryService;

  constructor(
    galleryService: GalleryService,
    placeholderService: PlaceholderService)
  {
    this.galleryService = galleryService;
    this.placeholderService = placeholderService;
  };
}

export class PlaceholderSource extends Source {
  private imgPlaceholder: ImagePlaceholder;

  constructor(
    imgPlaceholder: ImagePlaceholder,
    galleryService: GalleryService,
    placeholderService: PlaceholderService)
  {
    super(galleryService, placeholderService);
    this.imgPlaceholder = imgPlaceholder;
  }

  protected dragStartAllowed(event: Event): boolean
  {
    return !this.imgPlaceholder.isUnchanged() &&
           !this.imgPlaceholder.isPending();
  }

  public hasFocus(): boolean
  {
    return this.imgPlaceholder.hasFocus();
  }

  public callWithTransformationManager(
      impl: (m: TransformationManager) => void): void
  {
    this.imgPlaceholder.callWithTransformationManager(impl);
  }

  public getWrapper(): Svg
  {
    return this.imgPlaceholder.wrapper;
  }

  public getElement(): HTMLElement
  {
    return this.imgPlaceholder.element.node;
  }

  public showImageOverlay(): void
  {
    this.imgPlaceholder.showImageOverlay();
  }

  public hideImageOverlay(): void
  {
    this.imgPlaceholder.hideImageOverlay();
  }

  protected setDraggingImage(): void
  {
    this.galleryService.setDraggingImageId(
      this.imgPlaceholder.getImageModel().id);
  }

  protected async loadDraggingImage(targetPlaceholder: ImagePlaceholder):
      Promise<void>
  {
    if (this.imgPlaceholder.isPending()) {
      return;
    }

    const galleryImage = targetPlaceholder.getImageModel();

    const sourcePlaceholderLoad = galleryImage ?
      this.imgPlaceholder.loadGalleryImage(galleryImage.id) :
      this.imgPlaceholder.reset();

    await Promise.all([
      targetPlaceholder.loadGalleryImage(
        this.galleryService.getDraggingImageId()),
      sourcePlaceholderLoad]);
  }

  public isImageEditToolboxOpen(): boolean
  {
    return this.placeholderService.getActivePlaceholderToolbox() ===
        'ImageEditToolbox';
  }
}

export class GallerySource extends Source {
  private galleryImage: HTMLImageElement;
  private galleryImageId: string;

  constructor (
    galleryImage: HTMLImageElement,
    galleryService: GalleryService,
    placeholderService: PlaceholderService)
  {
    super(galleryService, placeholderService);
    this.galleryImage = galleryImage;
    this.galleryImageId = this.galleryImage.getAttribute('data-imageId');
  }

  protected dragStartAllowed(event: Event): boolean
  {
    return !this.galleryService.isImageUsed(this.galleryImageId);
  }

  public getWrapper(): Svg
  {
    const imgRect = this.galleryImage.getBoundingClientRect();
    const svgWrapper = SVG()
        .size(imgRect.width, imgRect.height)
        .viewbox(0, 0, imgRect.width, imgRect.height);
    svgWrapper.image(this.galleryImage.src)
        .size('100%', '100%')
        .attr('preserveAspectRatio', 'xMidYMid slice');
    return svgWrapper;
  }

  public getElement(): HTMLElement
  {
    return this.galleryImage;
  }

  protected setDraggingImage(): void
  {
    this.galleryService.setDraggingImageId(this.galleryImageId);
  }

  protected async loadDraggingImage(targetPlaceholder: ImagePlaceholder):
      Promise<void>
  {
    await targetPlaceholder.loadGalleryImage(this.galleryImageId);
  }
}

abstract class DragDropSVG
{
  protected readonly MIN_DRAG_DISTANCE: number = 1;
  protected source: any;
  protected element: HTMLElement;

  private mousedown: boolean = false;
  private dragging: boolean = false;

  protected start: Position;

  protected abstract dragStart(event: Event): void;
  protected abstract dragMove(event: Event): void;
  protected abstract dragEnd(event: Event): void;
  protected abstract dragStartAllowed(event: Event): boolean;

  constructor(source: any)
  {
    this.source = source;
    this.element = this.source.getElement();

    // FF is natively able to fire drag events within svgs - wohooo!
    // but for compatibility purposes we disable these built-in drag events :(
    this.element.addEventListener('dragstart', Utils.eventPreventDefault);

    this.element.addEventListener('mousedown', this.startHandler.bind(this));
    this.element.addEventListener('touchstart', this.startHandler.bind(this));

    this.element.getRootNode().addEventListener(
        'mousemove', this.moveHandler.bind(this));
    this.element.getRootNode().addEventListener(
        'touchmove', this.moveHandler.bind(this));

    this.element.getRootNode().addEventListener(
        'mouseup', this.endHandler.bind(this));
    this.element.getRootNode().addEventListener(
        'touchend', this.endHandler.bind(this));
  }

  public destructor(): void
  {
    this.element.removeEventListener('dragstart', Utils.eventPreventDefault);

    this.element.removeEventListener('mousedown', this.startHandler.bind(this));
    this.element.removeEventListener(
        'touchstart', this.startHandler.bind(this));

    this.element.getRootNode().removeEventListener(
        'mousemove', this.moveHandler.bind(this));
    this.element.getRootNode().removeEventListener(
        'touchmove', this.moveHandler.bind(this));

    this.element.getRootNode().removeEventListener(
        'mouseup', this.endHandler.bind(this));
    this.element.getRootNode().removeEventListener(
        'touchend', this.endHandler.bind(this));
  }

  private startHandler(event: Event): void
  {
    if (this.dragStartAllowed(event)) {
      this.start = this.getPosition(event);
      this.mousedown = true;
      Utils.eventPreventDefault(event);
    }
  }

  private initDragEvent(event: Event): boolean
  {
    const position = this.getPosition(event);
    return (
        !this.dragging &&
        this.mousedown && (
            Math.abs(position.clientX - this.start.clientX)
                > this.MIN_DRAG_DISTANCE ||
            Math.abs(position.clientY - this.start.clientY)
                > this.MIN_DRAG_DISTANCE)
    );
  }

  private moveHandler(event: Event): void
  {
    if (this.initDragEvent(event)) {
      this.dragStart(event);
      this.dragging = true;

      this.fireEvent(this.element, 'dragStart');
    }
    else if (this.dragging) {
      this.dragMove(event);
    }
  }

  private endHandler(event: Event): void
  {
    const wasTap = this.mousedown && !this.dragging;
    this.mousedown = false;
    if (this.dragging) {
      event.preventDefault();
      event.stopPropagation();

      this.dragging = false;

      this.dragEnd(event);
      this.fireEvent(this.element, 'dragEnd');
    }
    // On touch, the browser does not send a click when a drag could
    // have started. So for a simple tap (no drag), the click has to be sent
    // and it has to bubble up so the wrapperGroup's click handler runs
    else if (wasTap && (<TouchEvent>event).changedTouches) {
      this.element.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true }));
    }
  }

  protected getPosition(event: Event): Position
  {
    return {
        clientX: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].clientX
            : (<MouseEvent> event).clientX,
        clientY: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].clientY
            : (<MouseEvent> event).clientY,
        pageX: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].pageX
            : (<MouseEvent> event).pageX,
        pageY: (<TouchEvent> event).touches
            ? (<TouchEvent> event).touches[0].pageY
            : (<MouseEvent> event).pageY,
    };
  }

  protected fireEvent(element: HTMLElement, eventType: string): void
  {
    if (element) {
      const dropEvent = document.createEvent('MouseEvents');
      dropEvent.initEvent(eventType, false, true);
      element.dispatchEvent(dropEvent);
    }
  }
}

export class DragDropGhostSVG extends DragDropSVG
{
  protected readonly MIN_DRAG_DISTANCE: number = 25;

  private dragOverElement: HTMLElement;
  private ghost: HTMLElement;

  constructor(source: Source)
  {
    super(source);
  }

  protected dragStartAllowed(event: Event): boolean
  {
    if (this.source instanceof PlaceholderSource &&
        this.source.isImageEditToolboxOpen()) {
      return false;
    }

    return Utils.isLargeDevice() &&
           this.source.dragStartAllowed(event);
  }

  protected dragStart(event: Event): void
  {
    this.source.setDraggingImage();
    this.source.placeholderService.disableTextPlaceholders();
    this.ghost = this.createGhostImage(event);
  }

  protected dragMove(event: Event): void
  {
    const position = this.getPosition(event);

    this.ghost.style.left = position.pageX - this.ghost.offsetWidth / 2 + 'px';
    this.ghost.style.top = position.pageY - this.ghost.offsetHeight / 2 + 'px';

    const elementMouseIsOver =
        <HTMLElement> (<any>this.element.getRootNode()).elementFromPoint(
            position.clientX, position.clientY);
    if (this.dragOverElement !== elementMouseIsOver) {
      this.fireEvent(this.dragOverElement, 'dragleave');
      this.dragOverElement = elementMouseIsOver;
      this.fireEvent(this.dragOverElement, 'dragenter');
    }
  }

  protected dragEnd(event: Event): void
  {
    StageUtils.removeElement(this.ghost);
    this.source.placeholderService.enableTextPlaceholders();

    if (this.dragOverElement) {
      const dropEvent = document.createEvent('MouseEvents');
      dropEvent.initEvent('dropSwap', false, true);
      (<any> dropEvent).source = this.source;
      this.dragOverElement.dispatchEvent(<MouseEvent> dropEvent);
    }
  }

  private createGhostImage(event: Event): HTMLElement
  {
    const position = this.getPosition(event);
    const ghostContainerScale = 0.10;
    const rootNode = this.element.getRootNode() as Document | ShadowRoot;

    const stageSvgContainer =
        rootNode.querySelector('.stage-svg-container');
    const rectStage = stageSvgContainer.getBoundingClientRect();

    const wrapper = this.source.getWrapper();
    const svgClone = wrapper.node.cloneNode(true);

    const designStepContainer =
        rootNode.querySelector('.design-step-content');

    let ghost = document.createElement('div');
    ghost.classList.add('dragdrop-ghost-container');
    ghost.appendChild(svgClone);
    designStepContainer.appendChild(ghost);

    svgClone.setAttribute('width', '100%');
    svgClone.setAttribute('height', '100%');
    svgClone.setAttribute('preserveAspectRatio', 'xMidYMid slice');

    ghost.style.width =  rectStage.width * ghostContainerScale + 'px';
    ghost.style.left = Number(position.pageX - rectStage.width / 2) + 'px';
    ghost.style.top = Number(position.pageY - rectStage.height / 2) + 'px';

    let swapIcon = document.createElement('div');
    swapIcon.classList.add('dragdrop-ghost-swap-icon');
    ghost.appendChild(swapIcon);
    swapIcon.style.backgroundImage =
        `url(${Icons.url('replace')})`;

    return ghost;
  }
}

export class ImageDragGhostSVG extends DragDropSVG
{
  private coordinateScale: {x: number; y: number};

  constructor(source: PlaceholderSource)
  {
    super(source);
    this.element.style.cursor = 'grab';
  }

  protected dragStartAllowed(event: Event): boolean
  {
    return Utils.isLargeDevice() &&
           this.source.dragStartAllowed(event) &&
           this.source.hasFocus();
  }

  protected dragStart(event: Event): void
  {
    const wrapper = this.source.getWrapper();

    // Special rect is needed, because Chrome and Firefox are calculating
    // different bounding box of the wrapper.
    const wrapperViewBox = wrapper.viewbox();

    const rect = wrapper
      .rect()
      .move(wrapperViewBox.x, wrapperViewBox.y)
      .size(wrapperViewBox.width, wrapperViewBox.height);
    const placeholderRect = rect.node.getBoundingClientRect();
    rect.remove();
    this.coordinateScale = {
        x: placeholderRect.width / wrapperViewBox.width,
        y: placeholderRect.height / wrapperViewBox.height
    };

    this.source.showImageOverlay();
    this.element.style.cursor = 'grabbing';
  }

  protected dragMove(event: Event): void
  {
    const position = this.getPosition(event);
    const moveX = position.clientX - this.start.clientX;
    const moveY = position.clientY - this.start.clientY;

    this.source.callWithTransformationManager(m => {
      m.translate(
          moveX / this.coordinateScale.x * -1,
          moveY / this.coordinateScale.y * -1);
    });
    this.start = position;
  }

  protected dragEnd(event: Event): void
  {
    this.element.style.cursor = 'grab';
    this.source.hideImageOverlay();
  }

  public destructor(): void
  {
    super.destructor();
    this.element.style.cursor = '';
  }
}

export function applyContentImageSwapping(source: Source): DragDropGhostSVG
{
  return new DragDropGhostSVG(source);
}

export function enableImageDrag(source: PlaceholderSource
                                ): ImageDragGhostSVG
{
  return new ImageDragGhostSVG(source);
}
